import React from "react";
import {
  Box, Container, Heading, Text, VStack, SimpleGrid, Button,
  HStack, Icon, Badge, List, ListItem, ListIcon
} from "@chakra-ui/react";
import {
  FaHome, FaCheckCircle, FaArrowRight, FaPhone, FaKey,
  FaHammer, FaChartLine, FaHandshake
} from "react-icons/fa";
import { Link as RouterLink } from "react-router-dom";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const FINANCE_PRIMARY = "#141C49";
const FINANCE_ACCENT = "#C2A878";
const NAVY = "#141C49";

const loanTypes = [
  {
    icon: FaHome,
    title: "First Home Buyers",
    description: "Navigate your first purchase with confidence. We help you access grants, understand your deposit requirements, and get pre-approved — fast.",
    badge: "Popular"
  },
  {
    icon: FaKey,
    title: "Owner-Occupier",
    description: "Already own a home? We search 40+ lenders to make sure you're on the sharpest rate and the right loan structure for where you are in life.",
    badge: "Most Common"
  },
  {
    icon: FaHammer,
    title: "Construction Loans",
    description: "Build your dream home with progressive drawdown financing. We specialise in construction loan structures so you only pay interest as the build progresses.",
    badge: "Custom Build"
  },
  {
    icon: FaChartLine,
    title: "Investment Loans",
    description: "Buying to invest? We structure your loan to maximise deductibility and borrowing capacity — setting you up for long-term portfolio growth.",
    badge: "Wealth Building"
  }
];

const steps = [
  { number: "01", title: "Say hello", desc: "Get in touch — no obligation, no credit check. We learn about you and what you're trying to achieve." },
  { number: "02", title: "Understand your goals", desc: "We take the time to understand your situation, income, timeline, and the type of property you're after." },
  { number: "03", title: "Search the market", desc: "We compare rates and features across 40+ lenders to find the loan that suits your life — not a lender's margin." },
  { number: "04", title: "Handle the paperwork", desc: "We prepare and lodge your application, liaise with the lender, and keep you updated every step of the way." },
  { number: "05", title: "Get pre-approved", desc: "Know your borrowing power before you make an offer. Pre-approvals typically valid for 3 months." },
  { number: "06", title: "Settlement & beyond", desc: "We see it through to settlement — and stay in touch to make sure your loan keeps working for you." }
];

const FinanceServicesMortgage: React.FC = () => {
  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/finance/services/mortgage"
        fallbackTitle="Mortgage Brokerage - One Group Finance"
        fallbackDescription="Independent mortgage brokers searching 40+ lenders to find the right home loan for your situation. First home buyers, refinancing, investment, and construction loans."
      />
      <NavbarWithCallToAction />

      {/* Hero */}
      <Box
        position="relative"
        color="white"
        py={{ base: 20, md: 28 }}
        backgroundImage="url('/assets/finance-assets/asset 7.jpeg')"
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundAttachment={{ base: "scroll", md: "fixed" }}
        _before={{
          content: '""',
          position: "absolute",
          top: 0, left: 0, right: 0, bottom: 0,
          bg: "rgba(0, 31, 63, 0.85)",
          zIndex: 0
        }}
      >
        <Container maxW="container.xl" position="relative" zIndex={1}>
          <VStack spacing={6} textAlign="center">
            <Badge bg={FINANCE_ACCENT} color="white" fontSize="sm" px={4} py={1} borderRadius="full">
              Mortgage Brokerage
            </Badge>
            <Heading size="2xl" fontWeight="bold" maxW="3xl">
              Your Home Loan, Done Right
            </Heading>
            <Text fontSize="xl" color="gray.300" maxW="2xl">
              We're your independent lending expert — entirely free to you. We search 40+ lenders to find the loan that suits your life, not a bank's margin.
            </Text>
            <HStack spacing={4} pt={4} flexWrap="wrap" justify="center">
              <Button
                as={RouterLink}
                to="/finance/contact"
                size="lg"
                bg="white"
                color={NAVY}
                rightIcon={<FaArrowRight />}
                _hover={{ bg: FINANCE_ACCENT, color: "white" }}
                transition="all 0.2s"
              >
                Get Pre-Approved
              </Button>
              <Button
                size="lg"
                variant="outline"
                borderColor="white"
                color="white"
                leftIcon={<FaPhone />}
                _hover={{ bg: "rgba(255,255,255,0.1)" }}
                transition="all 0.2s"
              >
                1300 536 159
              </Button>
            </HStack>
          </VStack>
        </Container>
      </Box>

      {/* Intro */}
      <Box bg="gray.50" py={16}>
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={12} alignItems="center">
            <VStack align="flex-start" spacing={5}>
              <Text fontSize="sm" color={FINANCE_ACCENT} textTransform="uppercase" letterSpacing="wider" fontWeight="600">
                We work for you, not the banks
              </Text>
              <Heading size="xl" color={NAVY}>
                Your Personal, Independent Lending Expert
              </Heading>
              <Text color="gray.600" fontSize="lg" lineHeight="tall">
                One Group Finance is a mortgage broker, not a bank. That means we're entirely in your corner — searching the full market, removing the jargon, and making sure you walk away with the right loan at the right rate.
              </Text>
              <Text color="gray.600" fontSize="lg" lineHeight="tall">
                Finance can feel overwhelming, but you don't have to do it alone. We're with you from your first question through to settlement — and beyond.
              </Text>
            </VStack>
            <SimpleGrid columns={2} spacing={4}>
              {[
                { stat: "40+", label: "Lender Partners", sub: "Full market access" },
                { stat: "$72M", label: "Loans Settled", sub: "" },
                { stat: "48hrs", label: "Pre-Approval", sub: "Average turnaround" },
                { stat: "$0", label: "Upfront Fees", sub: "Paid by the lender" }
              ].map((item) => (
                <Box key={item.stat} p={6} bg="white" borderRadius="xl" shadow="sm" border="1px solid" borderColor="gray.100" textAlign="center">
                  <Text fontSize="3xl" fontWeight="bold" color={NAVY}>{item.stat}</Text>
                  <Text fontWeight="600" color="gray.700" fontSize="sm">{item.label}</Text>
                  <Text fontSize="xs" color="gray.500">{item.sub}</Text>
                </Box>
              ))}
            </SimpleGrid>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Loan Types */}
      <Container maxW="container.xl" py={20}>
        <VStack spacing={4} textAlign="center" mb={12}>
          <Heading size="xl" color="gray.800">Home Loan Solutions</Heading>
          <Text fontSize="lg" color="gray.600" maxW="2xl">
            Whether you're buying your first home, building, or investing — we have the right loan structure for you.
          </Text>
        </VStack>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
          {loanTypes.map((loan, idx) => (
            <Box
              key={idx}
              p={8}
              bg="white"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="xl"
              _hover={{ shadow: "xl", borderColor: FINANCE_ACCENT, transform: "translateY(-4px)" }}
              transition="all 0.3s"
            >
              <HStack justify="space-between" mb={4}>
                <Icon as={loan.icon} boxSize={10} color={FINANCE_ACCENT} />
                <Badge bg={FINANCE_ACCENT} color="white" fontSize="xs">{loan.badge}</Badge>
              </HStack>
              <Heading size="md" mb={3} color="gray.800">{loan.title}</Heading>
              <Text color="gray.600" lineHeight="tall">{loan.description}</Text>
            </Box>
          ))}
        </SimpleGrid>
      </Container>

      {/* Why Us */}
      <Box bg="gray.50" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={4} textAlign="center" mb={12}>
            <Heading size="xl" color={NAVY}>Why Choose One Group Finance?</Heading>
            <Text fontSize="lg" color="gray.600" maxW="xl">
              Simple. Flexible. Tailored to you.
            </Text>
          </VStack>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
            {[
              { icon: FaHandshake, title: "No Upfront Fees", desc: "We're paid by the lender when your loan settles. Our service costs you nothing." },
              { icon: FaCheckCircle, title: "Fast Pre-Approvals", desc: "Most clients receive pre-approval within 48 hours. Know your budget before you bid." },
              { icon: FaChartLine, title: "Ongoing Support", desc: "We review your loan annually to ensure it remains competitive. We're not done at settlement." }
            ].map((item, idx) => (
              <HStack
                key={idx}
                p={6}
                bg="white"
                borderRadius="xl"
                shadow="sm"
                border="1px solid"
                borderColor="gray.100"
                align="flex-start"
                spacing={4}
              >
                <Icon as={item.icon} boxSize={8} color={FINANCE_ACCENT} flexShrink={0} mt={1} />
                <Box>
                  <Heading size="sm" mb={2} color={NAVY}>{item.title}</Heading>
                  <Text fontSize="sm" color="gray.600">{item.desc}</Text>
                </Box>
              </HStack>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* Process */}
      <Container maxW="container.xl" py={20}>
        <VStack spacing={4} textAlign="center" mb={12}>
          <Heading size="xl" color="gray.800">How It Works</Heading>
          <Text fontSize="lg" color="gray.600" maxW="xl">Six simple steps from first hello to keys in hand.</Text>
        </VStack>
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
          {steps.map((step) => (
            <Box
              key={step.number}
              p={8}
              bg="white"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="xl"
              position="relative"
              _hover={{ shadow: "lg", borderColor: FINANCE_ACCENT }}
              transition="all 0.3s"
            >
              <Text
                fontSize="4xl"
                fontWeight="900"
                color={FINANCE_ACCENT}
                opacity={0.3}
                position="absolute"
                top={4}
                right={6}
                lineHeight={1}
              >
                {step.number}
              </Text>
              <Heading size="md" mb={3} color={NAVY}>{step.title}</Heading>
              <Text color="gray.600" fontSize="sm" lineHeight="tall">{step.desc}</Text>
            </Box>
          ))}
        </SimpleGrid>
      </Container>

      {/* CTA */}
      <Box bg={NAVY} color="white" py={20}>
        <Container maxW="container.xl" textAlign="center">
          <Heading size="xl" mb={4}>Ready to Find Your Perfect Home Loan?</Heading>
          <Text fontSize="lg" mb={8} color="gray.300" maxW="xl" mx="auto">
            No obligation. No credit check. Just a conversation with someone who genuinely wants to help you get there.
          </Text>
          <HStack justify="center" spacing={4} flexWrap="wrap">
            <Button
              as={RouterLink}
              to="/finance/contact"
              size="lg"
              bg={FINANCE_ACCENT}
              color="white"
              rightIcon={<FaArrowRight />}
              _hover={{ bg: "#a8905f" }}
              transition="all 0.2s"
            >
              Book a Free Consultation
            </Button>
            <Button
              size="lg"
              variant="outline"
              borderColor="white"
              color="white"
              leftIcon={<FaPhone />}
              _hover={{ bg: "rgba(255,255,255,0.1)" }}
              transition="all 0.2s"
            >
              1300 536 159
            </Button>
          </HStack>
        </Container>
      </Box>

      <FooterWithFourColumns />
    </Box>
  );
};

export default FinanceServicesMortgage;
